import { useEffect, useState, useCallback, useRef } from "react";
import { LoadingDiv, LoadingDivShort, SweetAlertShow } from "../master/Master";
import Header from "../master/SideBar";
import Nav from "../master/NavBar";
import Master from "./Master";
import { CardDiv2, ProgressBar } from "./components/Master";

import { staffAttendanceDashboard, staffLowAttendanceData } from "../../api/Attendance";
import DashboardModal from "./components/DashboardModal";

export default function StaffAttendanceDashboard() {
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    date: new Date().toISOString().split("T")[0],
    month: new Date().toISOString().slice(0, 7),
  });
  const [allFilters, setAllFilters] = useState({});
  const [data, setData] = useState({});

  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  const [analyticsData, setAnalyticsData] = useState({});

  // Track mount status + in-flight requests so we don't setState
  // on an unmounted component and don't leave stale requests running
  // when the user navigates away quickly.
  const isMounted = useRef(true);
  const abortRef = useRef(null);

  const buildQuery = useCallback((extra = {}) => {
    const params = new URLSearchParams();
    Object.entries({ ...filters, ...extra }).forEach(([key, value]) => {
      if (value) params.append(key, value);
    });
    return params.toString();
  }, [filters]);

  const getAttendance = useCallback(async (signal) => {
    setLoading(true);
    try {
      const response = await staffAttendanceDashboard("&" + buildQuery(), { signal });
      if (!isMounted.current) return;
      if (response.data.status) {
        setData(response?.data?.data);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      if (error?.name === "CanceledError" || error?.name === "AbortError") return;
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ?? error?.response?.message,
      );
    } finally {
      if (isMounted.current) setLoading(false);
    }
  }, [buildQuery]);

  const getAttendanceAnalytics = useCallback(async (signal) => {
    setLoadingAnalytics(true);
    try {
      const response = await staffLowAttendanceData("&" + buildQuery({ type: "count" }), { signal });
      if (!isMounted.current) return;
      if (response.data.status) {
        setAnalyticsData(response.data.data);
      } else {
        SweetAlertShow("info", "Warning", response.data.message);
      }
    } catch (error) {
      if (error?.name === "CanceledError" || error?.name === "AbortError") return;
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ?? error?.response?.message ?? error?.message
      );
    } finally {
      if (isMounted.current) setLoadingAnalytics(false);
    }
  }, [buildQuery]);

  useEffect(() => {
    isMounted.current = true;

    // Cancel any previous in-flight requests before firing new ones
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    // Fire both in parallel
    getAttendance(controller.signal);
    getAttendanceAnalytics(controller.signal);

    return () => {
      isMounted.current = false;
      controller.abort();
    };
  }, [filters, getAttendance, getAttendanceAnalytics]);

  return (
    <>
      {loading && <LoadingDiv />}
      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />
          <div className="layout-page">
            <Nav />
            <div className="content-wrapper">
              <div className="container-fluid flex-grow-1 container-p-y">
                <div className="row">
                  <Master />
                  <div className="col-12">
                    <div className="row align-items-center g-2">
                      <div className="col-7">
                        <h4 className="fw-bold mb-1">
                          Staff Attendance Dashboard
                        </h4>
                        <p className="text-muted mb-0">
                          Monitor daily attendance and analytics
                        </p>
                      </div>
                      <div className="col-5">
                        <div className="d-flex align-items-center gap-2 justify-content-end">
                          <div className="form-floating form-floating-outline">
                            <input
                              type="date"
                              className="form-control"
                              value={filters?.date}
                              onChange={(e) =>
                                setFilters({ ...filters, date: e.target.value })
                              }
                            />
                            <label htmlFor="date">Date</label>
                          </div>
                        </div>
                      </div>
                      <div className="col-12">
                        <div className="row g-4">
                          {[
                            {
                              label: "Total Staffs",
                              value: "",
                              showValue: data?.total || 0,
                              color: "primary",
                              icon: "ri-group-fill",
                            },
                            {
                              label: "Present",
                              value: "1",
                              showValue: data?.present || 0,
                              color: "success",
                              icon: "ri-checkbox-circle-line",
                            },
                            {
                              label: "Absent",
                              value: "2",
                              showValue: data?.absent || 0,
                              color: "danger",
                              icon: "ri-close-circle-line",
                            },
                            {
                              label: "Not Marked",
                              value: "0",
                              showValue: data?.not_marked || 0,
                              color: "warning",
                              icon: "ri-time-line",
                            },
                          ]?.map((type, index) => {
                            return (
                              <CardDiv2
                                key={index}
                                setFilters={setFilters}
                                filters={filters}
                                type={type}
                                onClick={() =>
                                  setAllFilters({
                                    ...filters,
                                    table: "staffAttendance",
                                    attendance: type?.value,
                                  })
                                }
                              />
                            );
                          })}
                        </div>
                      </div>
                      <div className="col-12">
                        <div className="row g-4">
                          <div className="col-lg-6">
                            <div className="card border-0 rounded-3">
                              <div className="card-header bg-white border-0 py-2">
                                <h6 className="fw-bold mb-0">
                                  Attendance Percentage
                                </h6>
                              </div>
                              <div className="card-body">
                                {[
                                  {
                                    label: "Present",
                                    progress: Number(((data?.present / data?.total) * 100).toFixed(2)) || 0,
                                    color: "success",
                                  },
                                  {
                                    label: "Absent",
                                    progress: Number(((data?.absent / data?.total) * 100).toFixed(2)) || 0,
                                    color: "danger",
                                  },
                                  {
                                    label: "Not Marked",
                                    progress: Number(((data?.not_marked / data?.total) * 100).toFixed(2)) || 0,
                                    color: "warning",
                                  },
                                ]?.map((type, index) => {
                                  return (
                                    <ProgressBar key={index} type={type} />
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                          <div className="col-lg-6">
                            <div className="card border-0 rounded-3">
                              <div className="card-header bg-white border-0 py-2">
                                <h6 className="fw-bold mb-0">Low Attendance</h6>
                              </div>
                              <div className="card-body">
                                <div className="row text-center g-3">
                                  <div
                                    className="col-md-6"
                                    data-bs-toggle="modal"
                                    data-bs-target="#MainModal"
                                    style={{ cursor: "pointer" }}
                                    onClick={() =>
                                      setAllFilters({
                                        ...filters,
                                        table: "staff",
                                        action: "low_attendance",
                                      })
                                    }
                                  >
                                    <div className="p-3 bg-danger-subtle rounded-3 card-transform">
                                      <h4 className="fw-bold text-danger mb-2">
                                        {loadingAnalytics ? (
                                          <LoadingDivShort />
                                        ) : (
                                          analyticsData?.low_attendance || 0
                                        )}
                                      </h4>
                                      <p className="mb-0">
                                        75% <b className="text-danger">↓</b>{" "}
                                      </p>
                                    </div>
                                  </div>
                                  <div
                                    className="col-md-6"
                                    data-bs-toggle="modal"
                                    data-bs-target="#MainModal"
                                    style={{ cursor: "pointer" }}
                                    onClick={() =>
                                      setAllFilters({
                                        ...filters,
                                        table: "staff",
                                        action: "long_absent",
                                      })
                                    }
                                  >
                                    <div className="p-3 bg-warning-subtle rounded-3 card-transform">
                                      <h4 className="fw-bold text-warning mb-2">
                                        {loadingAnalytics ? (
                                          <LoadingDivShort />
                                        ) : (
                                          analyticsData?.long_absent || 0
                                        )}
                                      </h4>
                                      <p className="mb-0">Long Absent</p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <DashboardModal
        setLoading={setLoading}
        allFilter={allFilters}
        setAllFilter={setAllFilters}
        refreshDashboard={() => {
          getAttendance();
        }}
      />
    </>
  );
}